<template>
  <div class="c-config-input-type u-block">
    <div class="c-config-input-info c-config-input-info-full">
      <label>{{ input.label }}</label>
      <p>{{ input.description }}</p>
    </div>

    <div
      class="c-code-field"
      :class="{ 'is-fullscreen': isFullscreen }"
    >
      <div
        v-if="isFullscreen"
        class="c-code-field__fullscreen-header"
      >
        <h3>{{ input.label }}</h3>
        <button
          type="button"
          class="ui-btn ui-btn--secondary"
          @click="toggleFullscreen"
        >
          Done
        </button>
      </div>

      <!-- page + language -->
      <div class="c-code-field__toolbar">
        <label class="c-code-field__page">
          <span>Page</span>
          <select
            v-model="selectedPage"
            class="ui-select"
          >
            <option
              v-for="page in availablePages"
              :key="page.id"
              :value="page.name"
            >
              {{ page.name }}
            </option>
          </select>
        </label>

        <div class="c-code-field__languages">
          <span class="c-code-field__toolbar-label">Language</span>
          <app-tabs
            variant="pills"
            label="Language"
            :tabs="languageTabs"
            :value="selectedLanguage"
            @input="selectedLanguage = $event"
          />
        </div>
      </div>

      <!-- editor -->
      <div class="c-code-field__editor">
        <div class="c-code-field__editor-header">
          <app-tabs
            label="Code"
            :tabs="codeTabs"
            :value="selectedCodeType"
            @input="selectedCodeType = $event"
          />

          <button
            type="button"
            class="c-code-field__icon-btn"
            :title="isFullscreen ? 'Exit full screen (Esc)' : 'Full screen'"
            :aria-label="isFullscreen ? 'Exit full screen' : 'Full screen'"
            @click="toggleFullscreen"
          >
            <app-icon :name="isFullscreen ? 'close_fullscreen' : 'open_in_full'" />
          </button>
        </div>

        <iframe
          v-if="selectedCodeType === 'preview'"
          class="c-code-field__preview"
          :style="{ height: editorHeight }"
          title="Preview"
          sandbox=""
          :srcdoc="previewDocument"
        />
        <code-editor
          v-else
          :key="`${selectedPage}-${selectedLanguage}-${selectedCodeType}-${isFullscreen}-${isNightMode}`"
          :value="getValue(selectedPage, selectedLanguage, selectedCodeType)"
          :language="currentCodeType.editorLanguage"
          :dark-theme="isNightMode"
          :height="editorHeight"
          line-wrapping
          :debounce="0"
          :read-only="disabled"
          :class="{ 'is-disabled': disabled }"
          @input="setValue(selectedPage, selectedLanguage, selectedCodeType, $event)"
        />

        <div class="c-code-field__footer">
          <span>
            Editing <strong>{{ selectedPage }}</strong> · <strong>{{ selectedLanguage.toUpperCase() }}</strong>
          </span>
          <span v-if="selectedCodeType === 'preview'">Preview shows HTML with CSS; scripts are not run.</span>
        </div>
      </div>
    </div>

    <div
      v-if="isFullscreen"
      class="c-code-field__backdrop"
      @click="toggleFullscreen"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { get, setWith } from 'lodash';
import AppIcon from '@/shared/app-icon/AppIcon';
import AppTabs from '@/shared/app-tabs/AppTabs';
import CodeEditor from '@/shared/code-editor/CodeEditor';

// Per-page, per-language code editor. Value shape (JSON string):
// { [page]: { [language]: { [codeType.key]: string } } }
export default {
  name: 'MultiLanguageCodeField',

  components: {
    AppIcon,
    AppTabs,
    CodeEditor,
  },

  props: {
    input: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    languages: {
      type: Array,
      required: true,
    },
    // [{ key: 'html', label: 'HTML', editorLanguage: 'html' }, ...]
    codeTypes: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      inputValue: {},
      selectedPage: 'default',
      selectedLanguage: this.languages[0],
      selectedCodeType: this.codeTypes[0].key,
      isFullscreen: false,
    };
  },

  computed: {
    ...mapGetters('customPages', ['customPages', 'customPagesFetched']),
    ...mapGetters('theme', ['isNightMode']),

    availablePages() {
      return [
        { id: 0, name: 'default' },
        ...this.customPages,
      ];
    },

    defaultInputValue() {
      try {
        return JSON.parse(get(this.input, 'defaultValue', {}));
      } catch (e) {
        return {};
      }
    },

    currentCodeType() {
      return this.codeTypes.find(codeType => codeType.key === this.selectedCodeType) || this.codeTypes[0];
    },

    // a dot marks languages that already have content on the selected page
    languageTabs() {
      return this.languages.map(language => ({
        key: language,
        label: language.toUpperCase(),
        dot: this.codeTypes.some(codeType => this.getValue(this.selectedPage, language, codeType.key).trim()),
      }));
    },

    codeTabs() {
      return [
        ...this.codeTypes.map(codeType => ({
          key: codeType.key,
          label: codeType.label,
          dot: !!this.getValue(this.selectedPage, this.selectedLanguage, codeType.key).trim(),
        })),
        { key: 'preview', label: 'Preview', icon: 'visibility' },
      ];
    },

    previewDocument() {
      const styles = this.codeTypes
        .filter(codeType => codeType.editorLanguage === 'css')
        .map(codeType => this.getValue(this.selectedPage, this.selectedLanguage, codeType.key))
        .join('\n');
      const html = this.getValue(this.selectedPage, this.selectedLanguage, 'html');

      return `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;font-family:sans-serif}${styles}</style></head><body>${html}</body></html>`;
    },

    editorHeight() {
      return this.isFullscreen ? 'calc(100vh - 320px)' : '460px';
    },
  },

  created() {
    const inputValue = get(this.input, 'value', this.defaultInputValue);
    this.inputValue = inputValue || {};

    if (!this.customPagesFetched) {
      this.getCustomPages();
    }
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown);
  },

  methods: {
    ...mapActions('customPages', ['getCustomPages']),

    getValue(page, language, type) {
      return get(
        this.inputValue,
        `${page}.${language}.${type}`,
        get(this.defaultInputValue, `${page}.${language}.${type}`, ''),
      ) || '';
    },

    setValue(page, language, type, value) {
      if (this.disabled) {
        return;
      }

      setWith(this.inputValue, `${page}.${language}.${type}`, value, Object);
      // new object so computed values (dots, preview) refresh
      this.inputValue = { ...this.inputValue };
      this.$emit('update', {
        value: JSON.stringify(this.inputValue),
      });
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;

      if (this.isFullscreen) {
        window.addEventListener('keydown', this.onKeydown);
      } else {
        window.removeEventListener('keydown', this.onKeydown);
      }
    },

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.toggleFullscreen();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.c-code-field {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  font-family: var(--font-sans);

  &.is-fullscreen {
    position: fixed;
    inset: 24px;
    z-index: $zIndexDialog + 1;
    margin: 0;
    padding: 24px;
    border-radius: var(--radius-xl);
    background-color: var(--bg-surface);
    box-shadow: var(--shadow-xl);
  }
}

.c-code-field__backdrop {
  position: fixed;
  inset: 0;
  z-index: $zIndexDialog;
  background: rgba(10, 11, 13, 0.55);
  backdrop-filter: blur(2px);
}

.c-code-field__fullscreen-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
  }
}

// ---------- toolbar ----------
.c-code-field__toolbar {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  flex-wrap: wrap;
}

.c-code-field__page {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 220px;
  margin: 0;

  > span {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
  }
}

.c-code-field__languages {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.c-code-field__toolbar-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
}

// ---------- editor card ----------
.c-code-field__editor {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background-color: var(--bg-surface);
  overflow: hidden;

  ::v-deep .code-editor-cm {
    border: none;
    border-radius: 0;
  }

  ::v-deep .is-disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.c-code-field__editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 8px;
  background-color: var(--bg-subtle);

  ::v-deep .c-app-tabs--underline {
    flex: 1;
    min-width: 0;
    border-bottom: none;

    .c-app-tabs__tab {
      height: 44px;
    }
  }
}

.c-code-field__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;

  &:hover {
    background-color: var(--bg-hover);
    color: var(--text-primary);
  }

  &:focus-visible {
    box-shadow: var(--focus-ring);
  }
}

.c-code-field__preview {
  width: 100%;
  border: 0;
  border-top: 1px solid var(--border-default);
  background-color: #fff;
}

.c-code-field__footer {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 16px;
  border-top: 1px solid var(--border-default);
  background-color: var(--bg-subtle);
  font-size: 12px;
  color: var(--text-muted);

  strong {
    font-weight: 600;
    color: var(--text-secondary);
  }
}
</style>
